import { AsideInfo } from '@components'

import { Divider, Flex, SkeletonBox, Space, Text } from '@billy/design-system'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { DATE_FORMAT, DATE_FORMAT_SHORT } from '@features/vatDeclarations/constants/dateFormat'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useLocale } from '../../../../../../hooks/useLocale'
import { formatDate } from '../../../../../../utils'
import { useVatDeclaration } from '../../../../context/vatDeclarationContext'
import { VatDeclarationStatus } from '../../../../enums/vatDeclarationStatus'
import { VatAmountDue } from './elements/VatAmountDue'
import { VatDeclarationsPeriodSidebarState } from './elements/VatDeclarationsPeriodSidebarState'
import { VatPaymentReferenceSection } from './elements/VatPaymentReferenceSection'
import { getDueDateLabel } from './utils/getDueDate'
import { getStatusKey } from './utils/getStatusKey'

export const VatDeclarationStatusCard = (): ReactElement | null => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const {
    isLoading,
    endDate,
    paymentDate,
    periodText,
    submission,
    startDate,
    status,
    totalAmount,
    vatDeclaration,
    isPeriodSettled,
  } = useVatDeclaration()

  const { code } = useLocale()

  const reportDeadline = vatDeclaration?.reportDeadline
  const dueDate = reportDeadline && getDueDateLabel({ locale: code, reportDeadline, status, t })
  const paymentDateFormatted =
    status === VatDeclarationStatus.Paid && paymentDate ? formatDate(paymentDate, DATE_FORMAT, code) : ''
  const shouldDisplayPaymentReferenceSection = organization?.countryId === 'NL'
  const displayedDate = paymentDateFormatted || dueDate
  const paymentReference = submission?.detail?.paymentReference || ''

  if (!vatDeclaration) {
    return null
  }

  return (
    <AsideInfo isDense>
      <VatDeclarationsPeriodSidebarState status={status} />
      <Space size="s4" />

      {/* VAT Amount */}
      <AsideInfo.Item>
        <Flex flexDirection="column" alignItems="center">
          {isLoading ? <SkeletonBox /> : <VatAmountDue amountDue={totalAmount} />}
          <Text variant="bodySM" colorVariant="secondary" alignment="center">
            {t('vat_declarations.sidebar.net_amount_title')}
          </Text>
        </Flex>
      </AsideInfo.Item>

      <Divider />

      {/* Due/Payment date */}
      <AsideInfo.Item>
        <Flex flexDirection="column" alignItems="center">
          <Space size="s12" />
          <Text variant="bodySM" colorVariant="secondary">
            {t(getStatusKey(status, isPeriodSettled))}
          </Text>
          <Space size="s4" />
          <Text weight="bold" alignment="center">
            {displayedDate}
          </Text>
        </Flex>
      </AsideInfo.Item>

      <Divider />

      {/* Period dates */}
      <AsideInfo.Item>
        <Flex flexDirection="column" alignItems="center">
          <Space size="s12" />
          <Text variant="bodySM" colorVariant="secondary" alignment="center">
            {periodText}
          </Text>
          <Space size="s4" />
          <Text weight="bold" alignment="center">
            {formatDate(startDate, DATE_FORMAT_SHORT, code)} - {formatDate(endDate, DATE_FORMAT_SHORT, code)}
          </Text>
        </Flex>
      </AsideInfo.Item>

      {/* Payment reference */}
      {shouldDisplayPaymentReferenceSection && (
        <>
          <Divider />
          <VatPaymentReferenceSection transactionId={paymentReference} />
        </>
      )}
    </AsideInfo>
  )
}
